import React from 'react'
import PropTypes from 'prop-types'
import {
  Page, View, Document, StyleSheet, Font
} from '@react-pdf/renderer'

import colors from '@settings/styleSettings'

import InterRegular from '@assets/fonts/Inter-Regular.ttf'
import InterBold from '@assets/fonts/Inter-Bold.ttf'
import { getOnlyDateFromFormattedDateStr } from '@util/timeUtil'
import { MainTitle } from './pdfComponents/MainTitle'
import { Line } from './pdfComponents/Line'
import { Footer } from './pdfComponents/Footer'
import ConsentText from './pdfComponents/ConsentText'
import { Signature } from './pdfComponents/Signature'
import Text from './pdfComponents/Text'

import InsurerEnum from '@util/InsurerEnum'

Font.register({ family: 'Inter-Regular', src: InterRegular })
Font.register({ family: 'Inter-Bold', src: InterBold })

// Create styles
const styles = StyleSheet.create({
  page: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    backgroundColor: '#FFFFFF',
  },
  section: {
    margin: '24 0',
  },
  container: {
    margin: '0 24',
    width: '100%',
  },
  firstPart: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  strong: {
    color: colors.strongGray,
    fontSize: 12,
    fontFamily: 'Inter-Bold',
  },
  text: {
    fontSize: 12,
  },
  signature: {
    color: colors.strongGray,
    fontSize: 12,
    textAlign: 'right',
  },
})

// Create Document Component
const InformedConsent = ({ beneficiary, insurance }) => {
  const insurer = InsurerEnum[insurance]
  const {
    acceptedTerms, name, cpf, assign
  } = beneficiary || {}
  const formattedDate = getOnlyDateFromFormattedDateStr(acceptedTerms)
  const formattedCpf = cpf?.replace(
    /^(\d{3})(\d{3})(\d{3})(\d{2})$/,
    '$1.$2.$3-$4'
  )
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.container}>
          <MainTitle text="Termo de consentimento" insurer={insurer} />
          <Line lineColor={colors.softGray} />
          <View style={{ marginTop: 20 }}>
            <Text style={[{ color: colors.strongGray }, styles.text]}>
              Este documento visa registrar a manifestação livre, informada e
              inequívoca pela qual eu,
              <Text style={styles.strong}>{name}</Text>
              , CPF n°
              <Text style={styles.strong}>
                {' '}
                {formattedCpf}
              </Text>
              , na qualidade
              de beneficiário titular de dados, adiante denominado TITULAR,
              concordo com o tratamento dos meus dados pessoais para finalidade
              específica de prestação de serviços de assistência
              médico-hospitalar conforme previsto na lei 9656/98, havida com a
              {' '}
              {insurer.name.toUpperCase()}
              , aqui na qualidade e adiante denominada Controladora,
              em conformidade com a Lei n. 13.709/2018 – Lei Geral de Proteção
              de Dados Pessoais (LGPD).
            </Text>
          </View>

          <ConsentText text="Ao manifestar minha aceitação para com o presente termo, dou meu consentimento e concordo que a Controladora tome decisões referentes ao tratamento de meus dados pessoais necessários ao usufruto dos serviços da Controladora, bem como realize o tratamento de tais dados, envolvendo operações como as que se referem a coleta, produção, recepção, classificação, utilização, acesso, reprodução, transmissão, distribuição, processamento, arquivamento, armazenamento, eliminação, avaliação ou controle da informação, modificação, comunicação, transferência, difusão ou extração." />
          <ConsentText
            title="1. Dados Pessoais: "
            text="A Controladora fica autorizada a tomar decisões referentes ao tratamento e a realizar o tratamento dos dados pessoais do Titular, imprescindíveis para manutenção da relação contratual."
          />
          <ConsentText
            title="2. Finalidades do Tratamento dos Dados: "
            text="O tratamento dos dados pessoais tem as seguintes finalidades: Permitir a identificação e o contato entre a Controladora e o Titular para fins de relacionamento comercial; Possibilitar que a Controladora elabore proposta comerciais para o Titular; Possibilitar que a Controladora forneça ao Titular os serviços dispostos no contrato de Plano de Saúde; Permitir que a Controladora alimente seu banco interno de cadastro de cliente, para fins de conhecimento do vínculo da relação comercial e a devida prestação do serviço contratado; Possibilitar que a Controladora utilize tais dados em Pesquisas de Mercado; Possibilitar que a Controladora utilize tais dados na inscrição, divulgação, premiação dos interessados participantes de campanhas ou eventos por ela promovidos; Possibilitar que a Controladora utilize tais dados na elaboração de relatórios e emissão de produtos e serviços; Possibilitar que a Controladora utilize tais dados para a emissão de Notas Fiscais e documentos financeiros correlatos."
          />
          <ConsentText
            title="3. Compartilhamento de Dados: "
            text="A Controladora fica autorizada a compartilhar os dados pessoais do Titular com outros agentes de tratamento de dados, caso seja necessário para as finalidades listadas neste termo e no contrato de Plano de Saúde, observados os princípios e as garantias estabelecidas pela Lei n.13.709/2018."
          />
          <ConsentText
            title="4. Segurança dos Dados: "
            text="A Controladora adotará todas as medidas de segurança, técnicas e administrativas aptas a proteger os dados pessoais de acessos não autorizados e de situações acidentais ou ilícitas de destruição, perda, alteração, comunicação ou qualquer forma de tratamento inadequado ou ilícito."
          />
          <ConsentText
            title="5. Término do Tratamento e Eliminação dos Dados: "
            text="A Controladora poderá manter e tratar os dados pessoais do Titular durante todo o período em que forem pertinentes ao alcance das finalidades listadas neste termo e dispostas em diplomas legais, para o interesse comum das partes pelo prazo legal."
          />
          <ConsentText
            title="6. Direitos do Titular: "
            text="O Titular tem direito a obter da Controladora, em relação aos dados por ele tratados, a qualquer momento e mediante requisição todos os direitos previstos no Art. 18 da Lei n.13.709/2018."
          />
          <ConsentText text="Este consentimento poderá ser revogado pelo Titular, a qualquer momento, mediante solicitação formal ao Controlador, ficando ciente de que poderá ser inviável à Controladora continuar o fornecimento de produtos ou serviços a partir da eliminação dos dados pessoais." />
          <View style={styles.section} wrap={false}>
            <Text style={styles.signature}>
              Assinado em
              {formattedDate}
            </Text>
          </View>
          <Signature image={assign} beneficiaryName={name} />
          <Line lineColor={colors.softGray} />
          <View style={styles.section} wrap={false}>
            {insurer.id === InsurerEnum['unimed-assis'].id ? (<Footer />) : null }
          </View>
        </View>
      </Page>
    </Document>
  )
}

InformedConsent.propTypes = {
  beneficiary: PropTypes.shape({
    name: PropTypes.string,
    cpf: PropTypes.string,
    acceptedTerms: PropTypes.string,
    assign: PropTypes.string,
  }).isRequired,
  insurance: PropTypes.string.isRequired,
}
InformedConsent.defaultProps = {
}

export default InformedConsent
